/**
 * DealDetailPage
 *
 * Read-only detail view for a single deal.
 * Shows deal header, value, stage, contact info, probability, and expected close date.
 *
 * Requirements: Task 6 - My Deals Page (Detail View)
 */

import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Button,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@bloomcode/ui';
import {
  Calendar,
  DollarSign,
  TrendingUp,
  Users,
  Briefcase,
  RefreshCw,
  Handshake,
} from '@bloomcode/icons';
import { formatCurrency } from '@bloomcode/utils';

import { useMyDeals } from '@/hooks/useMyDeals';

/**
 * Formats a date string to a readable format
 */
function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Formats a short date
 */
function formatShortDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export default function DealDetailPage() {
  const { id } = useParams<{ id: string }>();

  // Fetch all deals and find the one matching the ID
  // This leverages the cache from the list page
  const { deals, isLoading, error, refetch } = useMyDeals({ limit: 100 });

  const deal = useMemo(() => deals.find((d) => d.id === id) ?? null, [deals, id]);

  const stageName = deal?.stage?.data?.name ?? 'Unknown Stage';
  const stageColor = deal?.stage?.data?.color ?? '#6366f1';

  // Loading state
  if (isLoading && !deal) {
    return (
      <>
        <Helmet>
          <title>Deal Details | Bloomcode CRM Portal</title>
        </Helmet>
        <div className="container mx-auto p-6">
          <Breadcrumb className="mb-4">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/">Home</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/dashboard">Dashboard</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/deals">My Deals</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Deal Details</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          <div className="space-y-4">
            <div className="animate-pulse">
              <div className="h-8 w-64 rounded bg-muted mb-2" />
              <div className="h-5 w-32 rounded bg-muted" />
            </div>
            <Card>
              <CardContent className="p-6">
                <div className="animate-pulse space-y-4">
                  <div className="h-5 w-48 rounded bg-muted" />
                  <div className="h-5 w-36 rounded bg-muted" />
                  <div className="h-5 w-40 rounded bg-muted" />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </>
    );
  }

  // Error state
  if (error && !deal) {
    return (
      <>
        <Helmet>
          <title>Deal Details | Bloomcode CRM Portal</title>
        </Helmet>
        <div className="container mx-auto p-6">
          <Breadcrumb className="mb-4">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/">Home</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/dashboard">Dashboard</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/deals">My Deals</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Deal Details</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
              <p className="text-destructive mb-2">Failed to load deal details</p>
              <p className="text-sm text-muted-foreground mb-4">{error.message}</p>
              <Button variant="outline" onClick={() => refetch()}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Try Again
              </Button>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  // Not found state
  if (!deal && !isLoading) {
    return (
      <>
        <Helmet>
          <title>Deal Not Found | Bloomcode CRM Portal</title>
        </Helmet>
        <div className="container mx-auto p-6">
          <Breadcrumb className="mb-4">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/">Home</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/dashboard">Dashboard</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/deals">My Deals</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Deal Details</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
              <Handshake className="h-12 w-12 text-muted-foreground/40 mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">Deal Not Found</h3>
              <p className="text-muted-foreground">
                This deal may no longer be shared with you, or it may have been removed.
              </p>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  if (!deal) return null;

  return (
    <>
      <Helmet>
        <title>{deal.title} | Bloomcode CRM Portal</title>
      </Helmet>

      <div className="container mx-auto p-6">
        {/* Breadcrumb */}
        <Breadcrumb className="mb-4">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/">Home</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/dashboard">Dashboard</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/deals">My Deals</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Deal Details</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {/* Deal Header */}
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold text-foreground">{deal.title}</h1>
            <div className="mt-2 flex items-center gap-3">
              <Badge
                style={{
                  backgroundColor: `${stageColor}20`,
                  color: stageColor,
                  borderColor: `${stageColor}40`,
                }}
                className="border"
              >
                {stageName}
              </Badge>
              <span className="text-lg font-semibold text-foreground">
                {formatCurrency(deal.value, deal.currency)}
              </span>
            </div>
          </div>
        </div>

        {/* Deal Information Cards */}
        <div className="grid gap-6 md:grid-cols-2">
          {/* Deal Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Briefcase className="h-4 w-4 text-primary" />
                Deal Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Value</span>
                <span className="flex items-center gap-1.5 font-medium text-foreground">
                  <DollarSign className="h-3.5 w-3.5 text-muted-foreground" />
                  {formatCurrency(deal.value, deal.currency)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Probability</span>
                <span className="flex items-center gap-1.5 font-medium text-foreground">
                  <TrendingUp className="h-3.5 w-3.5 text-muted-foreground" />
                  {deal.probability}%
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Expected Close</span>
                <span className="flex items-center gap-1.5 font-medium text-foreground">
                  <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                  {formatShortDate(deal.expectedCloseDate)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Stage</span>
                <Badge
                  style={{
                    backgroundColor: `${stageColor}20`,
                    color: stageColor,
                    borderColor: `${stageColor}40`,
                  }}
                  className="border"
                >
                  {stageName}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Created</span>
                <span className="text-sm text-foreground">{formatShortDate(deal.createdAt)}</span>
              </div>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Users className="h-4 w-4 text-primary" />
                Contact Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {deal.contact?.data ? (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Name</span>
                    <span className="font-medium text-foreground">
                      {deal.contact.data.firstName} {deal.contact.data.lastName}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Email</span>
                    <span className="text-sm text-foreground">{deal.contact.data.email}</span>
                  </div>
                  {deal.contact.data.phone && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Phone</span>
                      <span className="text-sm text-foreground">{deal.contact.data.phone}</span>
                    </div>
                  )}
                  {deal.contact.data.company && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Company</span>
                      <span className="text-sm text-foreground">{deal.contact.data.company}</span>
                    </div>
                  )}
                  {deal.contact.data.position && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Position</span>
                      <span className="text-sm text-foreground">{deal.contact.data.position}</span>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Contact information is not available for this deal.
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Timeline / Dates */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Calendar className="h-4 w-4 text-primary" />
              Timeline
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Deal Created</span>
                <span className="text-sm text-foreground">{formatDate(deal.createdAt)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Last Updated</span>
                <span className="text-sm text-foreground">{formatDate(deal.updatedAt)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Expected Close Date</span>
                <span className="text-sm font-medium text-foreground">
                  {formatDate(deal.expectedCloseDate)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
